import React, { useState, useEffect, useRef, useContext } from "react";
import uuidv4 from "uuid/v4"; // random uuid
import classnames from "classnames";
import { UpdateStateContext, UpdateDispatchContext } from "components/SummonerProfiles/SummonerProfileUpdateReducer";
import { getSummonerProfileContext } from "components/SummonerProfiles/SummonerProfileContext";
import { useSummonerProfile } from "api/summonerProfileApi";
import { getSetAnonId } from "lib/analyticsHelper";
import { getWsUrl } from "lib/regionalApiUrlHelper";
import { useQuery, useLazyQuery } from "@apollo/client";
import { GET_USER_STATE, UPDATE_PLAYER_PROFILE } from "api/graphql/queries";
import { ReactComponent as Exclamation } from "svg/exclamation.svg";
import { ReactComponent as CheckMark } from "svg/check-line.svg";

const UpdateSpinner = (props) => {
  return (
    <div className="loadingio-spinner-dual-ring-3t4906g0bol">
      <div className="ldio-zx01oum11u">
        <div></div>
        <div>
          <div></div>
        </div>
      </div>
    </div>
  );
};

const UpdateButton = (props) => {
  const dispatch = useContext(UpdateDispatchContext);
  const updateState = useContext(UpdateStateContext);
  const { isUpdatingProfile, profileRefetches, onUpdateCompleted, onUpdateError } = updateState;
  // console.log(profileRefetches)
  const defaultMessage = "Update";
  const context = getSummonerProfileContext();
  const { summonerName, region } = context;
  const [state, setState] = useState({
    progress: 0,
    isUpdatingFinal: false,
    uuid: uuidv4(),
  });
  const [updatingSummoner, setUpdatingSummoner] = useState(false);
  const [message, setMessage] = useState(defaultMessage);
  const [updateComplete, setUpdateComplete] = useState(false);
  const { data: userState } = useQuery(GET_USER_STATE, { ssr: false });

  const heartbeat = useRef(null);
  const openedWS = useRef();
  let timeout;

  const [updatePlayerProfile, { loading, data: updatePlayerProfileData, error }] = useLazyQuery(UPDATE_PLAYER_PROFILE, {
    ssr: false,
    variables: {
      regionId: region,
      summonerName,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      console.log("completed!");
      // console.log(data);
      if (data.updatePlayerProfile.success) {
        // successfully updated user's profile
        finished();
      } else {
        // failed to update, set error message
        bad_request(data.updatePlayerProfile.errorReason);
      }
    },
    onError: (err) => {
      console.log(err);
    },
  });

  useEffect(() => {
    timeout = null;

    // Close open sockets on unmount
    return () => {
      if (openedWS.current && (openedWS.current.readyState === 2 || openedWS.current.readyState === 3)) {
        openedWS.current && openedWS.current.close();
      }
    };
  }, []);

  const { refetch: refetchSummonerProfile } = useSummonerProfile(region, summonerName, {}, null);
  useEffect(() => {
    if (isUpdatingProfile && updateComplete && Object.keys(profileRefetches)) {
      // console.log(profileRefetches)
      const entries = Object.entries(profileRefetches);
      const promises = entries.map(([key, fn]) => {
        try {
          return fn && fn();
        } catch (err) {
          return null;
        }
      });
      Promise.all(promises)
        .then((responses) => {
          dispatch({ type: "isUpdatingProfile", payload: false });
          const responseMap = {};
          entries.forEach(([key, value], index) => {
            responseMap[key] = responses[index];
          });
          onUpdateCompleted && onUpdateCompleted(responseMap);
        })
        .catch((error) => {
          console.log(error);
          dispatch({ type: "isUpdatingProfile", payload: false });
          onUpdateError && onUpdateError(error);
        });
    }
  }, [updateComplete, isUpdatingProfile, updateState]);

  useEffect(() => {
    dispatch({ type: "isUpdatingProfile", payload: false });
    setUpdatingSummoner(false);
    setUpdateComplete(false);
    setMessage(defaultMessage);
    setState({ ...state, alert: false });
    if (openedWS.current && (openedWS.current.readyState === 2 || openedWS.current.readyState === 3)) {
      openedWS.current && openedWS.current.close();
    }
  }, [summonerName, region]);

  const { uuid } = state;

  const startHeartbeat = (ws) => {
    // Send a heartbeat every 5 second
    // %Message{channel: "phoenix", topic: "conn", event: "heartbeat", message: %{}}
    heartbeat.current = setInterval(() => {
      ws.send(
        JSON.stringify({
          topic: `uuid:${uuid}`,
          event: "heartbeat",
          payload: {},
          ref: "https://b.u.gg",
        }),
      );
    }, 5000);
  };

  const stopHeartbeat = (ws) => {
    clearInterval(heartbeat.current);
  };

  const onUpdateMessage = (ws) => {
    // NOTE: We use what's in the path not on the page, just in case this user has updated their
    // username.
    let currentMatch = 0;
    let totalMissingMatches = 0;
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.event === "phx_reply") {
        // ok --> start updating
        setUpdatingSummoner(true);
        ws.send(
          JSON.stringify({
            topic: `uuid:${uuid}`,
            event: "update",
            payload: {
              summoner_name: summonerName,
              region_id: region,
              traffic_type: "website_click",
              user_state: userState && userState.getState,
              anon_id: getSetAnonId(),
            },
            ref: "https://b.u.gg",
          }),
        );
      } else if (data.event === "notify") {
        const { message, metadata } = data.payload;
        const { progress } = state;
        switch (message) {
          case "bad_request":
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => {
              setState({ ...state, isUpdatingFinal: false });
              setMessage(defaultMessage);
            }, 6000);
            setUpdatingSummoner(false);
            setMessage(`(Error Code: ${metadata})`);
            return setState({
              alert: true,
            });
          case "timeout":
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => {
              setState({ ...state, isUpdatingFinal: false });
              setUpdatingSummoner(false);
              setMessage(defaultMessage);
            }, 3000);
            setMessage(metadata > 0 ? `Please wait ${metadata} seconds to update again.` : `Please try again.`);
            setState({
              alert: true,
            });
            return;
          case "update_initialized": // Update officially starts here
            setState({
              ...state,
              isUpdatingFinal: true,
              alert: false,
              progress: (1 / 20) * 100,
            });
            setMessage(<UpdateSpinner />);
          case "updating_matches":
            currentMatch = 0;
            // setMessage(`Updating... 0 of ${metadata} matches`);
            totalMissingMatches = metadata;
            return;
          case "update_match": {
            currentMatch++;
            // setMessage(`Updating... ${currentMatch} of ${totalMissingMatches} matches`);
            return setState({
              ...state,
              progress: 0 + (currentMatch / totalMissingMatches) * 100,
            });
          }
          case "finished":
            setState({
              ...state,
              progress: 1 * 100,
            });
            dispatch({ type: "isUpdatingProfile", payload: true });
            refetchSummonerProfile();

            setTimeout(() => {
              setUpdatingSummoner(false);
              setUpdateComplete(true);
              setMessage(
                <div style={{ fontSize: "12px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                  Updated
                  <CheckMark style={{ marginLeft: "5px", height: "18px", width: "18px" }} />
                </div>,
              );
              if (timeout) clearTimeout(timeout);
              stopHeartbeat();
              setState({
                ...state,
                isUpdatingFinal: false,
                progress: 100,
              });
            }, 1000);

            setTimeout(() => {
              setUpdateComplete(false);
              setMessage("Update");
              setState({
                progress: 0,
                isUpdatingFinal: false,
                uuid: uuidv4(),
              });
            }, 2500);

          default:
            break;
        }
      }
    };
  };

  const init_progress_bar = () => {
    setState({
      ...state,
      isUpdatingFinal: true,
      alert: false,
      progress: (1 / 20) * 100,
    });
  };

  const bad_request = (message) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      setState({ ...state, isUpdatingFinal: false });
      setMessage(defaultMessage);
    }, 6000);
    setUpdatingSummoner(false);
    setMessage(`(Error Code: ${message})`);
    return setState({
      alert: true,
    });
  };

  const finished = () => {
    setState({
      ...state,
      progress: 1 * 100,
    });
    dispatch({ type: "isUpdatingProfile", payload: true });
    refetchSummonerProfile();

    setTimeout(() => {
      setUpdatingSummoner(false);
      setUpdateComplete(true);
      setMessage(
        <div style={{ fontSize: "12px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          Updated
          <CheckMark style={{ marginLeft: "5px", height: "18px", width: "18px" }} />
        </div>,
      );
      if (timeout) clearTimeout(timeout);
      stopHeartbeat();
      setState({
        ...state,
        isUpdatingFinal: false,
        progress: 100,
      });
    }, 1000);

    setTimeout(() => {
      setUpdateComplete(false);
      setMessage("Update");
      setState({
        progress: 0,
        isUpdatingFinal: false,
        uuid: uuidv4(),
      });
    }, 2500);
  };

  const startUpdateGraphQL = () => {
    updatePlayerProfile();
  };

  const initUpdateGraphQL = () => {
    setUpdateComplete(false);
    setState({ ...state, alert: false });
    setState({ ...state, isUpdatingFinal: true, alert: false });
    setMessage(<UpdateSpinner />);
    setUpdatingSummoner(true);
    init_progress_bar();
    startUpdateGraphQL();
  };

  const startUpdate = (ws) => {
    onUpdateMessage(openedWS.current);
    startHeartbeat(openedWS.current);
    ws.send(
      JSON.stringify({
        topic: `uuid:${uuid}`,
        event: "phx_join",
        payload: {},
        ref: "https://b.u.gg",
      }),
    );
  };

  const initUpdate = () => {
    setUpdateComplete(false);
    setState({ ...state, alert: false });
    setState({ ...state, isUpdatingFinal: true, alert: false });
    setMessage(<UpdateSpinner />);

    if (openedWS.current === undefined || (openedWS.current && openedWS.current.readyState === 3)) {
      const newOpenedWS = new WebSocket(`${getWsUrl()}/v1/ws/update/websocket`);
      newOpenedWS.onopen = (event) => {
        onUpdateMessage(openedWS.current);
        startUpdate(openedWS.current);
      };

      newOpenedWS.onclose = (event) => {
        switch (event.code) {
          case 1006: {
            setMessage("Connection error");
            setState({ ...state, alert: true });
            break;
          }
          default: {
            break;
          }
        }
      };
      openedWS.current = newOpenedWS;
    }

    if (openedWS.current && openedWS.current.readyState === 1) {
      onUpdateMessage(openedWS.current);
      startUpdate(openedWS.current);
    }
  };

  const { disableTooltip, outOfDate } = props;
  const { progress, isUpdatingFinal } = state;
  const disableUpdateButton = updatingSummoner || isUpdatingFinal || state.alert;
  const updateButtonClassNames = classnames("update-button", {
    "_is-updating": disableUpdateButton,
    success: updateComplete,
    error: state.alert,
  });

  return (
    <button className={updateButtonClassNames} onClick={initUpdateGraphQL} disabled={disableUpdateButton}>
      {(updatingSummoner || isUpdatingFinal) && <div className="progress-bar" style={{ width: `${progress}%` }} />}
      <div className="flex-center" style={{ position: "relative" }}>
        {message}
        {outOfDate && !disableUpdateButton && !updateComplete && (
          <div className="update-alert">
            <Exclamation />
          </div>
        )}
      </div>
    </button>
  );
};

export default UpdateButton;
